import { gql } from '@grandvisionhq/graphql';
import { deliveryEstimation } from './delivery-estimation.js';
import { insurance } from './insurance.js';

const appliedDiscountFragment = gql`
  fragment AppliedDiscountFragment on PriceBreakdownDiscount {
    appliedDiscounts {
      id
      type
      amount
      details {
        name {
          locale
          value
        }
        type
        value
      }
    }
  }
`;
const priceFragment = gql`
  fragment PriceFragment on OrderLineItemPrice {
    total {
      subtotal
      discount {
        amount
        percentage
      }
      net
      gross
      tax {
        amount
      }
    }
    unit {
      subtotal
      discount {
        amount
        percentage
        ...AppliedDiscountFragment
      }
      net
      gross
      tax {
        portions {
          rate
        }
        amount
      }
    }
  }
  ${appliedDiscountFragment}
`;
const solutionLineItemFragment = gql`
  fragment SolutionOrderLineItemFragment on OrderLineItemSolution {
    price {
      ...PriceFragment
    }
    lineItemNumber
    quantity
    product {
      brand
      mainImage
      modelCode
      isOnStock
      name {
        locale
        value
      }
      sku
      erpId
      currentProduct {
        type
        availabilityChannels {
          id
          isOnStock
          availableQuantity
        }
      }
      volume
      volumeUnit
    }
    isSubscription
    subscriptionDetails {
      deliveryInterval
    }
    insurance {
      ...InsuranceFragment
    }
    deliveryEstimation {
      ...DeliveryEstimationFragment
    }
  }
  ${priceFragment}
  ${insurance}
  ${deliveryEstimation}
`;
const contactLensLineItemFragment = gql`
  fragment ContactLensOrderLineItemFragment on OrderLineItemContactLens {
    price {
      ...PriceFragment
    }
    lineItemNumber
    quantity
    eyeSide
    prescription {
      addition
      axis
      baseCurve
      cylinder
      dominance
      diameter
      power
    }
    product {
      brand
      mainImage
      modelCode
      isOnStock
      erpId
      exclusiveBrand
      name {
        locale
        value
      }
      sku
      currentProduct {
        type
        availabilityChannels {
          id
          isOnStock
          availableQuantity
        }
      }
      boxQuantity
      lensType {
        locale
        value
      }
      material {
        locale
        value
      }
      replacementFrequency {
        locale
        value
      }
      colors {
        locale
        values
      }
    }
    isSubscription
    subscriptionDetails {
      deliveryInterval
    }
    insurance {
      ...InsuranceFragment
    }
    deliveryEstimation {
      ...DeliveryEstimationFragment
    }
  }
  ${priceFragment}
  ${insurance}
  ${deliveryEstimation}
`;
const prescriptionOpticalParametersGlassesFragment = gql`
  fragment PrescriptionOpticalParametersGlassesFragment on PrescriptionOpticalParametersGlasses {
    pupillaryDistance
    prism
    power
    cylinder
    axis
    addition
  }
`;
const prescriptionGlassLineItemFragment = gql`
  fragment PrescriptionGlassOrderLineItemFragment on OrderLineItemPrescriptionGlass {
    price {
      ...PriceFragment
    }
    lineItemNumber
    quantity
    configurationDetails {
      configurationType
      description {
        locale
        value
      }
      productPrice
      totalPaid
    }
    configurationItems {
      product {
        sku
        lensColor {
          locale
          value
        }
      }
    }
    prescription {
      ... on AddedGlassesPrescription {
        leftLens {
          ...PrescriptionOpticalParametersGlassesFragment
        }
        rightLens {
          ...PrescriptionOpticalParametersGlassesFragment
        }
      }
      ... on EmailPrescription {
        message
      }
      ... on ExistingPrescription {
        message
      }
      ... on FilePrescription {
        prescriptionFileName
        pupillaryDistanceFileName
      }
      ... on PrescriptionWithManualXORFilePupillaryDistance {
        leftLens {
          ...PrescriptionOpticalParametersGlassesFragment
        }
        prescriptionFileName
        optionalPupillaryDistanceFileName: pupillaryDistanceFileName
        rightLens {
          ...PrescriptionOpticalParametersGlassesFragment
        }
      }
    }
    mainLineItem {
      product {
        sku
        isOnStock
        exclusiveBrand
        erpId
        faceShape {
          locale
          values
        }
        frameType {
          locale
          value
        }
      }
    }
    product {
      brand
      mainImage
      modelCode
      erpId
      name {
        locale
        value
      }
      sku
      currentProduct {
        type
        availabilityChannels {
          id
          isOnStock
          availableQuantity
        }
      }
      productPrice
      totalPaid
    }
    insurance {
      ...InsuranceFragment
    }
    deliveryEstimation {
      ...DeliveryEstimationFragment
    }
  }
  ${priceFragment}
  ${prescriptionOpticalParametersGlassesFragment}
  ${insurance}
  ${deliveryEstimation}
`;
const nonPrescriptionBundleOrderLineItemFragment = gql`
  fragment NonPrescriptionBundleOrderLineItemFragment on OrderLineItemNonPrescriptionBundle {
    quantity
    lineItemNumber
    price {
      ...PriceFragment
    }
    product {
      sku
      isOnStock
      exclusiveBrand
      erpId
      currentProduct {
        type
        availabilityChannels {
          id
          isOnStock
          availableQuantity
        }
      }
      name {
        locale
        value
      }
      brand
      modelCode
      mainImage
      productPrice
      totalPaid
    }
    configurationDetails {
      configurationType
      description {
        locale
        value
      }
      totalPaid
      productPrice
    }
    insurance {
      ...InsuranceFragment
    }
    deliveryEstimation {
      ...DeliveryEstimationFragment
    }
  }
  ${priceFragment}
  ${insurance}
  ${deliveryEstimation}
`;
const accessoriesProductDetails = gql`
  fragment OrderedAccessoriesProductDetailsFragment on OrderedAccessoryAdditionalDetails {
    gender {
      locale
      values
    }
    material {
      locale
      value
    }
    accessoryType {
      locale
      value
    }
    accessoryColor {
      locale
      values
    }
  }
`;
const framesProductDetails = gql`
  fragment OrderedFramesProductDetailsFragment on OrderedFrameAdditionalDetails {
    gender {
      locale
      values
    }
    frameShape {
      locale
      value
    }
    frameMaterial {
      locale
      value
    }
    frameColor {
      locale
      values
    }
    frameType {
      locale
      value
    }
    faceShape {
      locale
      values
    }
  }
`;
const sunglassesProductDetails = gql`
  fragment OrderedSunglassesProductDetailsFragment on OrderedSunglassAdditionalDetails {
    gender {
      locale
      values
    }
    frameShape {
      locale
      value
    }
    frameMaterial {
      locale
      value
    }
    frameColor {
      locale
      values
    }
    frameType {
      locale
      value
    }
    faceShape {
      locale
      values
    }
  }
`;
const readyReadersProductDetails = gql`
  fragment OrderedReadyReadersProductDetailsFragment on OrderedReadyReaderAdditionalDetails {
    gender {
      locale
      values
    }
    frameShape {
      locale
      value
    }
    frameMaterial {
      locale
      value
    }
    frameColor {
      locale
      values
    }
    frameType {
      locale
      value
    }
    faceShape {
      locale
      values
    }
  }
`;
const productDataForAnalytics = gql`
  fragment ProductDataForAnalyticsFragment on OrderedProductRegular {
    exclusiveBrand
    isOnStock
    erpId
    additionalProductDetails {
      ...OrderedAccessoriesProductDetailsFragment
      ...OrderedFramesProductDetailsFragment
      ...OrderedSunglassesProductDetailsFragment
      ...OrderedReadyReadersProductDetailsFragment
    }
  }
  ${sunglassesProductDetails}
  ${readyReadersProductDetails}
  ${framesProductDetails}
  ${accessoriesProductDetails}
`;
const regularLineItemFragment = gql`
  fragment RegularOrderLineItemFragment on OrderLineItemRegular {
    price {
      ...PriceFragment
    }
    lineItemNumber
    quantity
    product {
      brand
      mainImage
      modelCode
      modelColorCode
      name {
        locale
        value
      }
      sku
      currentProduct {
        type
        availabilityChannels {
          id
          isOnStock
          availableQuantity
        }
      }
      ...ProductDataForAnalyticsFragment
    }
    isSubscription
    subscriptionDetails {
      deliveryInterval
    }
    insurance {
      ...InsuranceFragment
    }
    deliveryEstimation {
      ...DeliveryEstimationFragment
    }
  }
  ${priceFragment}
  ${productDataForAnalytics}
  ${insurance}
  ${deliveryEstimation}
`;

export { accessoriesProductDetails, appliedDiscountFragment, contactLensLineItemFragment, framesProductDetails, nonPrescriptionBundleOrderLineItemFragment, prescriptionGlassLineItemFragment, prescriptionOpticalParametersGlassesFragment, productDataForAnalytics, readyReadersProductDetails, regularLineItemFragment, solutionLineItemFragment, sunglassesProductDetails };
//# sourceMappingURL=line-item.js.map
